import {
  IAgendaData,
  IAgendaEvent,
  IArcBriefingData,
  IArcBriefingDataBullet,
  IMyClientsHealthData,
  IMyDiligenceData,
  IMyWorkData,
} from '@common/interfaces/dashboard'
import { IDealCleanupSuggestionsData } from '@common/interfaces/dealCleanup'
import { IMeetingBriefData } from '@common/interfaces/meetingBrief'
import { INotificationData } from '@common/interfaces/notification'
import { IProspectsAggregation } from '@common/interfaces/prospects'
import { Action, combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { createLoadingDataReducer } from '../../helpers/redux'
import { createRequestScopedReducer, IRequestScopedState } from '../../helpers/requestScopedReducer'
import { IActionWithParams } from '../apiMiddleware'
import { IUpdateCalendarEventParams, UPDATE_CALENDAR_EVENT_REQUEST } from '../calendar/actions'
import { ILoadingData } from '../types'
import * as actions from './actions'

/** One entry per calendar event, keyed by its Google event id. */
export type IMeetingBriefsState = Record<string, ILoadingData<IMeetingBriefData>>

export interface IDashboardState {
  loadingArray: string[]
  agenda: IAgendaState
  calendarRange: IAgendaState
  myWork: ILoadingData<IMyWorkData>
  myClientsHealth: ILoadingData<IMyClientsHealthData>
  myDiligence: ILoadingData<IMyDiligenceData>
  dashboardProspects: ILoadingData<IProspectsAggregation>
  dealCleanupSuggestions: ILoadingData<IDealCleanupSuggestionsData>
  waitingOnMe: ILoadingData<INotificationData>
  briefing: ILoadingData<IArcBriefingData>
  meetingBriefs: IMeetingBriefsState
}

/**
 * An agenda slice keyed by the days asked for, so stepping from Monday to Tuesday
 * while Monday is in flight can't leave Tuesday's header above Monday's meetings.
 */
export type IAgendaState = IRequestScopedState<IAgendaData>

const createAgendaReducer = <TParams>(
  types: string[],
  keyOf: (params: TParams | undefined) => string | null,
  extraCases?: (state: IAgendaState, action: IActionWithParams<TParams>) => IAgendaState,
) =>
  createRequestScopedReducer<IAgendaData, TParams>(types, {
    keyOf,
    isInitiallyLoading: true,
    extraCases,
  })

const agenda = createAgendaReducer<actions.IAgendaParams>(
  [actions.LIST_AGENDA_REQUEST, actions.LIST_AGENDA_SUCCESS, actions.LIST_AGENDA_FAILURE],
  (params) => params?.date ?? null,
)

/** The `requestedKey` a calendar range is stored under. */
export const calendarRangeKey = (params: actions.ICalendarRangeParams | undefined) =>
  params ? `${params.startDate}:${params.endDate}:${(params.userIds ?? []).join(',')}` : null

/**
 * A meeting given a new time moves as soon as the save goes out, so a dragged one
 * stays where it was dropped. Claiming the latest request drops a read already in
 * flight, which would put it back. The page refetches once the save settles, which
 * also puts back a move that failed.
 */
const applyNewTimes = (state: IAgendaState, action: IActionWithParams<unknown>): IAgendaState => {
  if (action.type !== UPDATE_CALENDAR_EVENT_REQUEST || !state.data) {
    return state
  }
  const { eventId, patch } = action.params as IUpdateCalendarEventParams
  if (!patch.start && !patch.end) {
    return state
  }
  const isMoved = (event: IAgendaEvent) => event.id === eventId && !event.owner
  if (!state.data.events.some(isMoved)) {
    return state
  }
  const events = state.data.events.map((event) =>
    isMoved(event)
      ? { ...event, start: patch.start ?? event.start, end: patch.end ?? event.end }
      : event,
  )
  return { ...state, latestRequestId: action.requestId, data: { ...state.data, events } }
}

const calendarRange = createAgendaReducer<actions.ICalendarRangeParams>(
  [
    actions.LIST_CALENDAR_RANGE_REQUEST,
    actions.LIST_CALENDAR_RANGE_SUCCESS,
    actions.LIST_CALENDAR_RANGE_FAILURE,
  ],
  calendarRangeKey,
  applyNewTimes,
)

/**
 * The entry a not-yet-fetched meeting reads as. `isLoading: true` is load-bearing,
 * not just a default: the modal treats "not loading and no data" as a failed fetch,
 * so starting false would flash an error on the first render, before the request
 * has even dispatched.
 */
export const EMPTY_MEETING_BRIEF: ILoadingData<IMeetingBriefData> = {
  isLoading: true,
  isSaving: false,
  isSaved: false,
  data: null,
}

interface IMeetingBriefAction extends Action {
  data?: IMeetingBriefData
  params?: { eventId?: string }
}

/**
 * Keyed by event rather than a single shared slice, mirroring `networkRails` in
 * the CRM reducer.
 *
 * This is what makes concurrent fetches safe: briefs generate on demand and can
 * take seconds, so opening meeting A, closing it, then opening a cached meeting B
 * lets A's response land last. Against one shared slice that response would
 * overwrite B's and leave B's modal permanently blank. Keyed by id, A's response
 * lands in A's entry and cannot touch B.
 *
 * There is deliberately no reset action: entries are kept so a reopened meeting
 * has its brief on hand. Rendering it instantly is the modal's half of that
 * bargain — it shows the cached brief while it reads it again on open, and polls
 * only while one is unsettled.
 */
const meetingBriefs = (
  state: IMeetingBriefsState = {},
  action: IMeetingBriefAction,
): IMeetingBriefsState => {
  const eventId = action.params?.eventId

  switch (action.type) {
    case actions.GET_MEETING_BRIEF_REQUEST: {
      if (!eventId) {
        return state
      }
      // Spread the previous entry rather than resetting it, as `networkRails`
      // does: a refetch must not throw away a brief we already have, or a request
      // that then fails would leave the modal with nothing to show.
      const prev = state[eventId] ?? EMPTY_MEETING_BRIEF
      return { ...state, [eventId]: { ...prev, isLoading: true } }
    }
    case actions.REGENERATE_MEETING_BRIEF_REQUEST: {
      if (!eventId) {
        return state
      }
      const prev = state[eventId] ?? EMPTY_MEETING_BRIEF
      return { ...state, [eventId]: { ...prev, isSaving: true } }
    }
    case actions.REGENERATE_MEETING_BRIEF_FAILURE: {
      if (!eventId) {
        return state
      }
      return {
        ...state,
        [eventId]: { ...(state[eventId] ?? EMPTY_MEETING_BRIEF), isSaving: false },
      }
    }
    case actions.GET_MEETING_BRIEF_SUCCESS:
    case actions.REGENERATE_MEETING_BRIEF_SUCCESS: {
      if (!eventId || !action.data) {
        return state
      }
      return {
        ...state,
        [eventId]: { isLoading: false, isSaving: false, isSaved: false, data: action.data },
      }
    }
    case actions.GET_MEETING_BRIEF_FAILURE: {
      if (!eventId) {
        return state
      }
      return {
        ...state,
        [eventId]: { ...(state[eventId] ?? EMPTY_MEETING_BRIEF), isLoading: false },
      }
    }
    default:
      return state
  }
}

// Refresh is passed as a *saving* action so the current briefing stays on
// screen while a new one generates, rather than flashing to a skeleton.
const briefingRequests = createLoadingDataReducer<IArcBriefingData>(
  [actions.GET_BRIEFING_REQUEST],
  [actions.REFRESH_BRIEFING_REQUEST],
)

type IBriefingState = ILoadingData<IArcBriefingData>

/** Rewrites the bullets, but only of the brief an action was aimed at, never one regenerated since. */
const updateBullets = (
  state: IBriefingState,
  generatedAt: string | undefined,
  update: (bullets: IArcBriefingDataBullet[]) => IArcBriefingDataBullet[],
): IBriefingState =>
  state.data && state.data.generatedAt === generatedAt
    ? { ...state, data: { ...state.data, bullets: update(state.data.bullets) } }
    : state

const setBulletCompleted =
  (index: number | undefined, completed: boolean) => (bullets: IArcBriefingDataBullet[]) =>
    bullets.map((bullet) => (bullet.index === index ? { ...bullet, completed } : bullet))

/**
 * A check is a toggle (set on `_REQUEST`, undone on `_FAILURE`). `_SUCCESS` re-applies it rather
 * than taking the server's snapshot, which may predate a later check or a task being completed.
 */
const briefing = (
  state: IBriefingState | undefined,
  action: IActionWithParams<Partial<actions.IBriefingBulletCompletedParams>, IArcBriefingData>,
): IBriefingState => {
  const current = briefingRequests(state, action)
  const { index, completed = false, generatedAt } = action.params ?? {}

  switch (action.type) {
    case actions.SET_BRIEFING_BULLET_COMPLETED_REQUEST:
    case actions.SET_BRIEFING_BULLET_COMPLETED_SUCCESS:
    case actions.MARK_BRIEFING_TASK_COMPLETED:
      return updateBullets(current, generatedAt, setBulletCompleted(index, completed))
    case actions.SET_BRIEFING_BULLET_COMPLETED_FAILURE:
      return updateBullets(current, generatedAt, setBulletCompleted(index, !completed))
    default:
      return current
  }
}

export default combineReducers({
  loadingArray: loadingArrayReducer([actions.prefix]),
  agenda,
  calendarRange,
  myWork: createLoadingDataReducer<IMyWorkData>([actions.LIST_MY_WORK_REQUEST]),
  myClientsHealth: createLoadingDataReducer<IMyClientsHealthData>([
    actions.LIST_MY_CLIENTS_HEALTH_REQUEST,
  ]),
  myDiligence: createLoadingDataReducer<IMyDiligenceData>([actions.LIST_MY_DILIGENCE_REQUEST]),
  dashboardProspects: createLoadingDataReducer<IProspectsAggregation>([
    actions.LIST_DASHBOARD_PROSPECTS_REQUEST,
  ]),
  dealCleanupSuggestions: createLoadingDataReducer<IDealCleanupSuggestionsData>(
    [actions.LIST_DEAL_CLEANUP_SUGGESTIONS_REQUEST],
    [actions.RESPOND_TO_DEAL_CLEANUP_SUGGESTION_REQUEST],
  ),
  briefing,
  meetingBriefs,
})
